import {
  Button,
  Dialog,
  DialogContent,
  Form,
  Progress,
  SubmitButton,
  toast,
} from "@bleu.builders/ui";
import { ArrowLeftIcon } from "@radix-ui/react-icons";
import React, { useEffect } from "react";
import { useWizard } from "react-use-wizard";

import { cn } from "#/lib/utils";

import { steps } from "./OnboardingValidation";

export const OnboardingWizardWrapper = ({ children, steps, form }) => {
  const { goToStep, activeStep, isFirstStep, isLastStep } = useWizard();

  const label = steps.find((step, idx) => idx === activeStep).label;

  const progress = 100 * (activeStep / steps.length) + 5;

  useEffect(() => {
    if (isLastStep || isFirstStep) return;

    // names of the fields for the current step
    const stepFields = steps
      .find((step, idx) => idx === activeStep)
      .fields.map((field) => field.name);

    const subscription = form.watch((values, { name, type }) => {
      // check if all fields for the current step are filled
      const allFieldsFilled = stepFields.every(
        (fieldName) => values[fieldName] !== "" && values[fieldName] != null,
      );

      if (allFieldsFilled) {
        goToStep(activeStep + 1);
      }
    });

    return () => subscription.unsubscribe(); // cleanup on unmount
  }, [form.watch, activeStep, steps]);

  return (
    <Dialog aria-labelledby="onboarding" open>
      <DialogContent
        onPointerDownOutside={(e) => e.preventDefault()}
        closeButton={<></>}
        aria-label="subscribe"
        className="p-2 flex flex-col gap-4 bg-background dark:border-2 rounded-lg shadow-xl pb-10 sm:max-w-[550px] lg:max-w-[800px]"
      >
        <div className="flex flex-col mt-8 ml-2">
          <span className="ml-8 text-sm font-semibold">
            Falta pouco para começarmos a otimizar a sua rotina. Finalize o
            Onboarding!
          </span>
          <div className="flex items-center mt-4">
            {!isFirstStep && (
              <button onClick={() => goToStep(activeStep - 1)}>
                <ArrowLeftIcon className="size-5 font-bold mr-2" />
              </button>
            )}
            <Progress
              value={progress}
              className={cn("w-2/5 bg-gray-300 shadow-sm", {
                "ml-8": isFirstStep,
              })}
            />
          </div>
          <div className="ml-8 mt-4">
            <span className="font-semibold leading-none tracking-tight">
              {label}
            </span>
            <Form {...form} className="h-full w-2/3 mt-2" method="post">
              {children}
            </Form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const OnboardingWizardStep = ({ children, form, onSubmit }) => {
  const { nextStep, isLastStep, isFirstStep, activeStep } = useWizard();

  const brazilPhoneRegex =
    /^(?:(?:\+|00)?(55)\s?)?\(?([1-9][0-9])\)?\s?((?:9\d|[2-9])\d{3})\-?(\d{4})$/;

  const handleNext = async () => {
    const isValid = await form.trigger();
    if (!isValid) return;

    if (activeStep === 0) {
      const phone = form.getValues("phone_number");
      if (!brazilPhoneRegex.test(phone)) {
        toast({
          title: "Telefone inválido",
          description: "Por favor, insira um telefone válido.",
          variant: "destructive",
        });
        return;
      }
    }

    nextStep();
  };

  const handleSubmit = async () => {
    const formValues = form.getValues();

    const requiredFields = Object.values(steps).reduce((acc, step) => {
      return acc.concat(step.fields);
    }, []);

    const hasEmptyFields = requiredFields.some((field) =>
      nullOrUndefinedOrEmptyString(formValues[field]),
    );

    if (hasEmptyFields) {
      toast({
        title: "Informações faltando",
        description: "Por favor, preencha os campos para continuar.",
        variant: "destructive",
      });
      return;
    }
    onSubmit();
  };

  function nullOrUndefinedOrEmptyString(value) {
    return value === null || value === undefined || value === "";
  }

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === "Enter") {
        Object.entries(steps).forEach(([key, value]) => {
          if (Number(key) === activeStep) {
            const fieldValues = value.fields.map((field) =>
              form.getValues(field),
            );
            if (fieldValues.some((val) => nullOrUndefinedOrEmptyString(val))) {
              toast({
                title: value.toastMessage.title,
                description: value.toastMessage.description,
                variant: value.toastMessage.variant,
              });
            } else if (value.finalStep) {
              handleSubmit();
            } else {
              handleNext();
            }
          }
        });
      }
    };

    window.addEventListener("keydown", handleKeyPress);

    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [handleNext]);

  return (
    <div className="flex size-full flex-col">
      <div>{children}</div>
      <div className="mt-5">
        {isLastStep && (
          <SubmitButton type="button" onClick={handleSubmit}>
            Concluir
          </SubmitButton>
        )}
        {isFirstStep && (
          <Button
            type="button"
            onClick={() => handleNext()}
            disabled={isLastStep}
            className="ml-auto"
          >
            Continuar
          </Button>
        )}
      </div>
    </div>
  );
};
